import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Typography, Stack } from '@mui/material';
import { toast } from 'react-toastify';

import { FBoxContainer, FHButton, FHPageLink, FProgressBar } from '../../components/base';
import { behaviorActions } from '../../store';
import PageContainer from '../../components/layout/PageContainer';

import type { RootState } from '../../store';
import type { IBehavior } from '../../utils/types';

function GoalTemplatePage() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const {
    lastFetchedGoalTemplate: currentGoalTemplate,
    list: behaviorList,
    loading,
  } = useSelector((state: RootState) => state.behaviors);

  const { goalTemplateId, behaviorId } = useParams();

  useEffect(() => {
    dispatch(behaviorActions.getList({ getAccessTokenSilently }));
  }, []);

  useEffect(() => {
    if (!goalTemplateId || !behaviorId) return;

    dispatch(
      behaviorActions.getGoalTemplate({ getAccessTokenSilently, behaviorId, goalTemplateId }),
    );

    return () => {
      dispatch(behaviorActions.clearLastFetchedGoalTemplate());
    };
  }, [goalTemplateId, behaviorId]);

  const changeGoalTemplateStatus = async ({ isActive }: { isActive: boolean }) => {
    if (!currentGoalTemplate || !currentGoalTemplate?.behavior?.id || !currentGoalTemplate?.id)
      return;
    const { error } = await dispatch(
      behaviorActions.updateGoalTemplateStatus({
        getAccessTokenSilently,
        behaviorId: currentGoalTemplate?.behavior?.id,
        goalTemplateId: currentGoalTemplate?.id,
        isActive,
      }),
    );
    if (!error) {
      if (isActive) {
        toast.success('The current goal template has been activated');
      } else {
        toast.success('The current goal template has been deactivated');
      }
    }
  };

  return (
    <PageContainer>
      {(!currentGoalTemplate || loading) && <FProgressBar />}
      <Box sx={{ mt: 3 }} textAlign={'left'}>
        <FHPageLink to="/admin" label="Admin Home" />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h3">{currentGoalTemplate?.label}</Typography>
          </Box>

          <Stack direction="row" spacing={2}>
            {currentGoalTemplate?.is_active ? (
              <FHButton
                disabled={loading}
                onClick={() => changeGoalTemplateStatus({ isActive: false })}
              >
                Deactivate
              </FHButton>
            ) : (
              <FHButton
                disabled={loading}
                onClick={() => changeGoalTemplateStatus({ isActive: true })}
              >
                Activate
              </FHButton>
            )}
          </Stack>
        </Box>
        <hr style={{ margin: '2rem 0' }}></hr>
        <Box>
          <FBoxContainer sx={{ height: '100%', borderRadius: '20px', boxShadow: 'none' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <Typography variant="h6" mb={2}>
                Goal Template Info
              </Typography>
              <Box p={0.5}>
                <Typography
                  display="inline-block"
                  mr={2}
                  sx={{ minWidth: '80px' }}
                  fontWeight={700}
                >
                  Name:
                </Typography>
                <Typography display="inline">{currentGoalTemplate?.label}</Typography>
              </Box>
              <Box p={0.5}>
                <Typography
                  display="inline-block"
                  mr={2}
                  sx={{ minWidth: '80px' }}
                  fontWeight={700}
                >
                  Prefix:
                </Typography>
                <Typography display="inline">{currentGoalTemplate?.prefix}</Typography>
              </Box>
              <Box p={0.5}>
                <Typography
                  display="inline-block"
                  mr={2}
                  sx={{ minWidth: '80px' }}
                  fontWeight={700}
                >
                  FocusArea:
                </Typography>
                <Typography display="inline">
                  {
                    behaviorList.find((b: IBehavior) => b?.id == currentGoalTemplate?.behavior?.id)
                      ?.label
                  }
                </Typography>
              </Box>
              <Box p={0.5}>
                <Typography
                  display="inline-block"
                  mr={2}
                  sx={{ minWidth: '80px' }}
                  fontWeight={700}
                >
                  Status:
                </Typography>
                <Typography display="inline">
                  {currentGoalTemplate?.is_active ? 'Active' : 'Inactive'}
                </Typography>
              </Box>
            </Box>
          </FBoxContainer>
        </Box>
      </Box>
    </PageContainer>
  );
}

export default GoalTemplatePage;
